import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useApp } from "../context/AppContext";

const CATEGORIES = ["all", "video", "interview", "exhibition", "workshop", "press"];

const CAT_LABELS = {
  en: { all: "All", video: "Videos", interview: "Interviews", exhibition: "Exhibitions", workshop: "Workshops", press: "Press" },
  ar: { all: "الكل", video: "مقاطع فيديو", interview: "مقابلات", exhibition: "معارض", workshop: "ورش عمل", press: "الصحافة" },
};

export default function MediaPress({ embedded = false }) {
  const { lang, t } = useApp();
  const isAr = lang === "ar";
  const T = t("mediaPress") || {};

  const [items, setItems]       = useState([]);
  const [filter, setFilter]     = useState("all");
  const [loading, setLoading]   = useState(true);
  const [player, setPlayer]     = useState(null);
  const [embedError, setEmbedError] = useState(false);

  useEffect(() => {
    supabase.from("media_press").select("*").eq("is_visible", true)
      .order("sort_order").order("created_at", { ascending: false })
      .then(({ data }) => { setItems(data ?? []); setLoading(false); });
  }, []);

  const visible = filter === "all" ? items : items.filter(i => i.category === filter);
  const playerIdx = player !== null ? visible.findIndex(i => i.id === player) : -1;

  const openPlayer = useCallback((item) => {
    setPlayer(item.id);
    setEmbedError(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const closePlayer = () => setPlayer(null);

  const prevVideo = () => {
    if (playerIdx > 0) setPlayer(visible[playerIdx - 1].id);
  };
  const nextVideo = () => {
    if (playerIdx < visible.length - 1) setPlayer(visible[playerIdx + 1].id);
  };

  const currentItem = player !== null ? visible[playerIdx] : null;

  function embedUrl(url) {
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
    return url;
  }

  const title  = item => isAr && item?.title_ar  ? item.title_ar  : item?.title;
  const desc   = item => isAr && item?.description_ar ? item.description_ar : item?.description;

  return (
    <div style={embedded ? {} : { maxWidth: 1100, margin: "0 auto", padding: "40px 20px 60px" }}>
      {!embedded && (
        <>
          <h1 className="page-title" style={{ marginBottom: 6 }}>{T.title || (isAr ? "الإعلام والصحافة" : "Artists Media & Press")}</h1>
          <p style={{ color: "var(--text-muted,#888)", marginBottom: 28, fontSize: "0.95rem" }}>
            {T.subtitle || (isAr ? "مقاطع فيديو، مقابلات، معارض وورش عمل" : "Videos, interviews, exhibitions and workshops")}
          </p>
        </>
      )}

      {/* Player panel */}
      {currentItem && (
        <div style={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 32, overflow: "hidden" }}>
          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            {embedError ? (
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#111", gap: 16 }}>
                <p style={{ color: "#aaa", fontSize: "0.9rem" }}>{isAr ? "تعذّر تشغيل الفيديو هنا" : "Video can't be played here"}</p>
                <a href={currentItem.url} target="_blank" rel="noopener noreferrer"
                  style={{ background: "#ff0000", color: "#fff", padding: "10px 22px", borderRadius: 6, fontWeight: 700, textDecoration: "none", fontSize: "0.9rem" }}>
                  {isAr ? "مشاهدة على يوتيوب" : "Watch on YouTube"} ↗
                </a>
              </div>
            ) : (
              <iframe
                key={currentItem.id}
                src={embedUrl(currentItem.url)}
                title={title(currentItem)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onError={() => setEmbedError(true)}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
              />
            )}
          </div>
          <div style={{ padding: "16px 20px", display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "#e0d8d0", marginBottom: 4 }} dir={isAr ? "rtl" : "ltr"}>{title(currentItem)}</div>
              {desc(currentItem) && <div style={{ fontSize: "0.875rem", color: "#888", lineHeight: 1.6 }} dir={isAr ? "rtl" : "ltr"}>{desc(currentItem)}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              <a href={currentItem.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ background:"#ff0000", color:"#fff", textDecoration:"none" }}>YT ↗</a>
              <button onClick={prevVideo} disabled={playerIdx <= 0} className="btn btn-sm" style={{ opacity: playerIdx <= 0 ? 0.3 : 1 }}>&#8592; {isAr ? "السابق" : "Prev"}</button>
              <button onClick={nextVideo} disabled={playerIdx >= visible.length - 1} className="btn btn-sm" style={{ opacity: playerIdx >= visible.length - 1 ? 0.3 : 1 }}>{isAr ? "التالي" : "Next"} &#8594;</button>
              <button onClick={closePlayer} className="btn btn-sm btn-ghost">&#10005;</button>
            </div>
          </div>
        </div>
      )}

      {/* Category filter */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => { setFilter(cat); setPlayer(null); }}
            className={`btn btn-sm${filter === cat ? " btn-primary" : " btn-ghost"}`}
          >
            {CAT_LABELS[lang][cat]}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: "#666" }}>{isAr ? "جارٍ التحميل…" : "Loading…"}</p>}

      {!loading && visible.length === 0 && (
        <p style={{ color: "#666" }}>{isAr ? "لا توجد مقاطع في هذا القسم بعد." : "No media in this category yet."}</p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 }}>
        {visible.map(item => (
          <div
            key={item.id}
            onClick={() => openPlayer(item)}
            style={{ cursor: "pointer", background: "var(--card-bg,#111)", border: "1px solid var(--border,#2a2a2a)", borderRadius: 10, overflow: "hidden", transition: "transform 0.15s", outline: player === item.id ? "2px solid var(--brand,#c9a84c)" : "none" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}
          >
            <div style={{ position: "relative", paddingTop: "56.25%", background: "#1a1a1a" }}>
              {item.thumbnail_url
                ? <img src={item.thumbnail_url} alt={title(item)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, color: "#444" }}>🎬</div>}
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)", opacity: 0, transition: "opacity 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                <div style={{ width: 50, height: 50, borderRadius: "50%", background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#fff" }}>&#9654;</div>
              </div>
              <span style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.7)", color: "#c9a84c", fontSize: 10, padding: "2px 7px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {CAT_LABELS[lang][item.category] || item.category}
              </span>
            </div>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--text,#e0d8d0)", lineHeight: 1.4, marginBottom: 4 }} dir={isAr ? "rtl" : "ltr"}>{title(item)}</div>
              {desc(item) && <div style={{ fontSize: "0.8rem", color: "#888", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }} dir={isAr ? "rtl" : "ltr"}>{desc(item)}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
